import { Dispatch, SetStateAction, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BackButton, Badge, Tabs, TabsType, Typography } from '../../components';
import { PUBLISH_PARAM } from '../../constants';
import { useAppSelector, useCheckRequiredProspectFields } from '../../hooks';
import { ComponentSize, Permissions, ProspectPageTabs, ProspectType, StatusColor } from '../../types';
import { camelCaseToLabel } from '../../utils';

interface ProspectPageHeaderProps {
  activeTab: ProspectPageTabs;
  visitedTabs: Set<ProspectPageTabs>;
  setActiveTab: Dispatch<SetStateAction<ProspectPageTabs>>;
  setVisitedTabs: Dispatch<SetStateAction<Set<ProspectPageTabs>>>;
}

const ProspectPageHeader = ({ activeTab, visitedTabs, setActiveTab, setVisitedTabs }: ProspectPageHeaderProps) => {
  const [searchParams] = useSearchParams();
  const isPublishing = searchParams.get(PUBLISH_PARAM) === 'true';

  const {
    fields: { isPromptEdited },
    isDraft,
    type,
  } = useAppSelector((state) => state.prospect);

  const user = useAppSelector((state) => state.auth.user);
  const permissions = user?.permissions || [];

  const isSuperAdmin = permissions.includes(Permissions.ADMIN_ACCESS);
  const canManageSims = permissions.includes(Permissions.MANAGE_PROSPECT);

  const showPromptEditedBadge = isSuperAdmin && isPromptEdited && type !== ProspectType.CUSTOM;

  const areRequiredFieldsFilled = useCheckRequiredProspectFields();

  // Maps each tab to an object containing its id, title, and disabled state.
  // Hide the Prompt tab from non-super-admins.
  const tabs = useMemo(
    () =>
      Object.values(ProspectPageTabs)
        .filter((tab) => {
          // Show the Prompt tab if the user is a super-admin and the prospect is not a custom prospect,
          // because custom prospects already have the prompt in the scenario tab.
          const showPromptTab = type !== ProspectType.CUSTOM && isSuperAdmin;
          // Show config tab if:
          // 1. The user is super admin
          // 2. The user is publishing a draft
          // 3. The user is editing an existing simulation and has manage prospect permissions
          const showConfigTab = isSuperAdmin || isPublishing || (!isDraft && canManageSims);
          return (
            (showPromptTab || tab !== ProspectPageTabs.PROMPT) &&
            (showConfigTab || tab !== ProspectPageTabs.CONFIGURATION)
          );
        })
        .map((tab, index) => {
          // Show an asterisk if the tab has unfilled fields.
          const showAsterisk = !areRequiredFieldsFilled(tab, true);

          // The tab is disabled if it has not been visited before and all required fields in preceding tabs have not been filled out.
          const prevTab = index > 0 ? Object.values(ProspectPageTabs)[index - 1] : null;
          const isDisabled =
            !visitedTabs.has(tab) &&
            (tab === ProspectPageTabs.PROMPT || (!!prevTab && !areRequiredFieldsFilled(prevTab)));
          return {
            id: tab,
            title: (
              <Typography>
                <span className="font-mono">{'\u00A0'}</span>
                {camelCaseToLabel(tab)}
                <span className="font-mono">{showAsterisk ? '*' : '\u00A0'}</span>
              </Typography>
            ),
            disabled: isDisabled,
          };
        }),
    [isSuperAdmin, visitedTabs, areRequiredFieldsFilled, type, isDraft, canManageSims]
  );

  // Changes the active tab and updates the visited tabs.
  const handleChangeTab = (newActiveTab: string) => {
    setActiveTab(newActiveTab as ProspectPageTabs);
    setVisitedTabs((prev) => new Set(prev).add(newActiveTab as ProspectPageTabs));
  };

  return (
    <div className="flex w-full flex-none items-center justify-between gap-4 px-8 pt-4">
      <div className="flex items-center gap-4">
        <BackButton />
        <Tabs activeTab={activeTab} setActiveTab={handleChangeTab} type={TabsType.BORDERED} tabs={tabs} />
      </div>
      {showPromptEditedBadge && <Badge label="Prompt edited" size={ComponentSize.LARGE} color={StatusColor.ERROR} />}
    </div>
  );
};

export default ProspectPageHeader;
